import { Router } from 'next/router';
import { CountryCodes } from '@bridebook/toolbox/src/gazetteer';
import { Slug, UrlFriendlySlug } from '@bridebook/toolbox/src/types';
import { removeForwardSlash } from '@bridebook/web/lib/utils/strings';
import { IGuestListUrlParams, isEventId, SettingsSection, TShortlistTab } from './types';
import { isEmpty, pickBy } from 'lodash';
import { InboxView } from '@bridebook/web/components/inbox/context/types';
import { WeddingWebsiteOgImageParams } from '../app-basic/lib/wedding-website/metadata/get-og-image-url';
import {
  SupplierSwipingAnalyticsLocation,
  SupplierSwipingAnalyticsTriggerLocation,
} from '@bridebook/web/lib/supplier-finding-experience/types';

/**
 * An object with URLs to protected pages (only accessible for logged-in users).
 * It's a part of the UrlHelper object as well as a reference for _middleware redirecting.
 */
export const ProtectedUrls = {
  bookmarks: '/bookmarks',
  budget: {
    main: '/budget',
    drawer: (name: string, id: string) => `/budget/${name}-${id}`,
  },
  checklist: {
    main: '/checklist',
    task: (name: string, id: string) => `/checklist/${name}-${id}`,
  },
  guestlist: {
    main: '/guestlist',
    events: `/guestlist?${new URLSearchParams({ tabId: 'events' }).toString()}`,
    eventDetails: (eventId: string) =>
      `/guestlist?${new URLSearchParams({ tabId: eventId }).toString()}`,
    withFilters: (params: IGuestListUrlParams) => {
      const searchParams = new URLSearchParams();

      // Handle tabId - if eventId is provided but no tabId, use eventId as tabId
      const tabId =
        params.tabId ||
        (params.eventId && params.eventId !== 'overview' ? params.eventId : undefined);
      if (tabId && tabId !== 'overview') {
        searchParams.set('tabId', tabId);
        if (isEventId(tabId)) {
          searchParams.set('eventId', tabId);
        }
      }

      const { edit } = params;

      if (edit) {
        searchParams.set('edit', 'true');
      }

      // Add filter parameters
      Object.entries(params).forEach(([key, value]) => {
        if (!['tabId', 'eventId', 'isEdit'].includes(key) && !isEmpty(value)) {
          searchParams.set(key, String(value));
        }
      });

      const queryString = searchParams.toString();
      return `/guestlist${queryString ? `?${queryString}` : ''}`;
    },
  },
  website: {
    main: '/website',
    mainWithSource: (source: string) => `/website?source=${source}`,
    weddingDetails: {
      main: (source?: string) => `/website/wedding-details${source ? `?source=${source}` : ''}`,
      sectionAnchor: (sectionName: string) => {
        const sufix = sectionName ? `#${encodeURIComponent(sectionName)}` : '';
        return `${UrlHelper.website.weddingDetails.main()}${sufix}`;
      },
      itemEdit: (sectionName: string, itemId: string) =>
        `/website/wedding-details/${encodeURIComponent(sectionName)}/${itemId}`,
      itemAdd: (sectionName: string) =>
        `/website/wedding-details/${encodeURIComponent(sectionName)}/add`,
      giftList: {
        main: '/website/wedding-details/gift-list',
      },
      hotels: {
        main: '/website/wedding-details/hotels',
      },
    },
    privacy: '/website/privacy',
    preview: (source?: string) => `/website/preview${source ? `?source=${source}` : ''}`,
    design: '/website/design',
  },
  events: {
    main: '/events',
    details: (eventId: string, returnTo?: 'guestlist') =>
      `/events/${eventId}${returnTo ? `?${new URLSearchParams({ returnTo }).toString()}` : ''}`,
    new: (returnTo?: 'guestlist' | 'wedding-details') =>
      `/events/new${returnTo ? `?${new URLSearchParams({ returnTo }).toString()}` : ''}`,
  },
  home: '/home',
  inbox: {
    // there is no /inbox main page, it's tab navigation so either /inbox/suppliers or /inbox/guests. It has to be here for the urlHelper and logged out middleware logic to work.
    main: '/inbox',
    [InboxView.Suppliers]: {
      main: '/inbox/suppliers',
      conversation: (convId: string) => `/inbox/${InboxView.Suppliers}/${convId}`,
    },
    [InboxView.Archived]: {
      main: '/inbox/archived',
      conversation: (convId: string) => `/inbox/${InboxView.Archived}/${convId}`,
    },
    [InboxView.Guests]: {
      main: '/inbox/guests',
      campaign: (campaignId: string) => `/inbox/${InboxView.Guests}/${campaignId}`,
    },
    [InboxView.MessageCenter]: `/inbox/${InboxView.MessageCenter}`,
  },
  onboarding: '/onboarding',
  collaboratorOnboarding: '/collaborator-onboarding',
  planning: '/planning',
  recommendations: '/recommendations',
  scrapbook: '/scrapbook',
  savedInspiration: '/scrapbook/inspiration',
  inspiration: '/inspiration',
  inspiration2: '/inspiration-2',
  settings: {
    iconSwitch: '/settings/account-details#icon-switch',
    inviteCollaborator: '/settings/invite-collaborator',
    main: '/settings',
    manageCollaborators: '/settings/manage-collaborators',
    tab: (tab: SettingsSection) => ({
      href: `/settings?tab=${tab}`,
      as: `/settings/${tab}`,
    }),
    accountDetails: '/settings/account-details',
  },
  suppliers: {
    main: '/suppliers',
    supplier: (slug: string, supplierQueryId: string) => `/wedding-${slug}/${supplierQueryId}`,
    priceEstimate: (slug: string, supplierQueryId: string) =>
      `/wedding-${slug}/${supplierQueryId}${PRICE_ESTIMATE_PATH_SUFFIX}`,
  },
  shortlist: {
    tab: (tab: TShortlistTab) => ({
      href: `/shortlist?tab=${tab}`,
      as: `/shortlist/${tab}`,
    }),
    tabBySupplierType: (supplierType?: Slug) => {
      const tab = !supplierType || supplierType === 'venue' ? 'venues' : 'suppliers';
      return {
        href: `/shortlist?tab=${tab}`,
        as: `/shortlist/${tab}`,
      };
    },
    main: '/shortlist',
    supplierDetails: (id: string, supplierType: Slug, shortlistTab?: TShortlistTab) => {
      const tab = shortlistTab || (supplierType === 'venue' ? 'venues' : 'suppliers');
      return {
        href: `/shortlist?category=${tab}&supplierId=${id}`,
        as: `/shortlist/${tab}/${id}`,
      };
    },
    // For now we can only compare venues
    comparison: '/shortlist/venues/comparison',
  },
  specialOffers: '/special-offers',
  supplier: '/supplier',
  quizzes: '/quizzes',
  weddingFairs: '/wedding-fairs',
  globalLocations: '/global-locations',
  swiping: {
    category: (
      slug: UrlFriendlySlug,
      analytics?: {
        location: SupplierSwipingAnalyticsLocation;
        triggerLocation: SupplierSwipingAnalyticsTriggerLocation;
      },
    ) => {
      const { location, triggerLocation } = analytics ?? {};
      const params = [
        location ? `analyticsLocation=${location}` : '',
        triggerLocation ? `analyticsTriggerLocation=${triggerLocation}` : '',
      ]
        .filter(Boolean)
        .join('&');
      return `/swiping/${slug}${params ? '?' + params : ''}`;
    },
    main: '/swiping',
  },
};

/**
 * An object with URLs to pages available only to logged-out
 */
export const GuestsOnlyUrls = {
  changePassword: '/change-password',
  login: '/signup?mode=login',
  signup: '/signup',
  collaboration: '/accept-collaboration-invite',
  signupCordova: '/signup?welcome=show',
};

/**
 * An object with URLs to pages accessible for both logged in/out user).
 */
export const PublicUrls = {
  addressCollector: {
    main: '/address-collector',
    guestForm: (weddingId: string) => `/address-collector/${weddingId}`,
  },
  acceptCollaborationInvite: '/accept-collaboration-invite',
  article: {
    id: (articleId: string) => '/article/' + articleId,
    main: '/article',
  },
  author: {
    id: (authorId: number) => '/author-page/' + authorId,
    main: '/author-page',
  },
  index: '/',
  landing: '/landing',
  reviews: '/reviews',
  search: '/search',
  weddingIdeasInspiration: '/wedding-ideas-inspiration',
  weddingPlanningTools: {
    main: '/wedding-planning-tools',
    guestlist: '/wedding-planning-tools/guestlist',
    shortlist: '/wedding-planning-tools/supplier-shortlist',
    budget: '/wedding-planning-tools/budget',
    checklist: '/wedding-planning-tools/checklist',
  },
  weddingWebsite: {
    main: '/wedding-website',
    templatesThemes: '/wedding-website/templates-themes',
    customiseYourWebsite: '/wedding-website/customise-your-website',
    ogImage: (params: WeddingWebsiteOgImageParams) => {
      const filteredParams = pickBy(params, (value) => value != null && value !== '');
      return `/api/wedding-website/image.png?${new URLSearchParams(filteredParams).toString()}`;
    },
  },
  weddingGiftLists: '/wedding-gift-lists',
  bridesmaidDresses: '/bridesmaid-dresses',
  weddingSuppliers: '/wedding-suppliers',
  advice: '/advice',
  terms: '/terms',
  privacyPolicy: '/privacy-policy',
  cookiePolicy: '/cookie-policy',
  aboutUs: '/about-us',
  contactUs: '/contact-us',
  pressAndMedia: '/pressandmedia',
  appSupport: '/app-support',
  error: '/404',
  // This path is used in firebase authentication email templates.
  // Do not remove it unless it's changed in firestore.
  accountActionHandler: '/account-action-handler',
  for: {
    main: '/for',
    rsvp: (websiteId: string, queryParams?: Partial<{ guest: string; guestId: string }>) => {
      let queryString = '';
      if (queryParams && Object.keys(queryParams).length > 0) {
        const params = new URLSearchParams();
        Object.entries(queryParams).forEach(([key, value]) => {
          if (value) params.append(key, value);
        });
        queryString = `?${params.toString()}`;
      }
      return `/for/${websiteId}/rsvp${queryString}`;
    },
  },
};

export const PRICE_ESTIMATE_PATH_SUFFIX = '/price-estimate';

/**
 * Manages all the URLs in one place.
 * Add a simple string or an object with "href" and "as" for URLs with params.
 */
export const UrlHelper = {
  ...PublicUrls,
  ...GuestsOnlyUrls,
  ...ProtectedUrls,
};

const PathsWithHiddenMobileNav = {
  supplierProfile: '/wedding-',
  articles: '/article',
  onboarding: '/onboarding',
  collaboratorOnboarding: '/collaborator-onboarding',
  signup: '/signup',
  swiping: UrlHelper.swiping.main,
  weddingWebsiteDetails: '/website/wedding-details',
  weddingWebsitePreview: '/website/preview',
  weddingWebsiteEvents: '/events',
  weddingWebsiteDesign: '/website/design',
  weddingWebsiteGiftList: UrlHelper.website.weddingDetails.giftList.main,
  weddingWebsiteHotels: UrlHelper.website.weddingDetails.hotels.main,
};

/* ############################################################################
 *  UTILS
 * ######################################################################### */

// These pages are available only for logged-in users
export const loggedInOnlyPages = Object.values(ProtectedUrls)
  .map((url) => (typeof url === 'string' ? url : url.main))
  .map(removeForwardSlash);

export const getIsMobileNavShown = (pathname: string, marketPrefix: string) =>
  !Object.values(PathsWithHiddenMobileNav).some((path) =>
    pathname.startsWith(`/${marketPrefix}${path}`),
  );

// These pages are available only for logged-out guests
export const loggedOutOnlyPages = Object.values(GuestsOnlyUrls).map(removeForwardSlash);

// Returns true if a given path is included in ProtectedUrls
export const getIsProtectedPage = (path: Router['pathname']) => {
  const cleanPath = removeForwardSlash(path.replace('/[market]', ''));
  return loggedInOnlyPages.some((url) => cleanPath.startsWith(url));
};

export const getContactFormHelpUrl = (countryCode: CountryCodes) => {
  switch (countryCode) {
    case CountryCodes.AT:
    case CountryCodes.CH:
    case CountryCodes.DE:
      return 'https://share.hsforms.com/1CUvOH5GOTFyK0XdC_wbDAQ1p8l9';
    case CountryCodes.FR:
      return 'https://share.hsforms.com/1LugYkDu7RJy_wXxCUAE3Tw1p8l9';
    case CountryCodes.PL:
      return 'https://share.hsforms.com/159yW_8tTSa2i3EJ_5tebwg1p8l9';
    default:
      return 'https://share.hsforms.com/1SmbWSJYtSOGpM-mC5KZaZQ1p8l9';
  }
};

export const getKnowledgeBaseUrl = (countryCode: CountryCodes) => {
  switch (countryCode) {
    case CountryCodes.AT:
    case CountryCodes.CH:
    case CountryCodes.DE:
      return 'https://support.bridebook.com/de/support';
    case CountryCodes.FR:
      return 'https://support.bridebook.com/fr/support';
    case CountryCodes.PL:
      return 'https://support.bridebook.com/migration/en/support-pl';
    default:
      return 'https://support.bridebook.com/en/support';
  }
};

export const getCareersUrl = (ref: string) => `https://career.bridebook.com/?ref=${ref}`;

export const INVITES_AND_PAPER_URL = 'http://invites.bridebook.com/';
